import React, { useState, useEffect } from 'react';
import api from '../api';
import { BarChart3, DollarSign, Loader2, RefreshCw, AlertTriangle, TrendingUp, Phone, Globe, Cpu, Mic, Brain, Zap, Plus, User } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import AnimatedDropdown from './AnimatedDropdown';

const CostDashboard = () => {
  const [costAnalytics, setCostAnalytics] = useState(null);
  const [pricingConfig, setPricingConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [newPricing, setNewPricing] = useState({
    service_type: '',
    service_name: '',
    pricing_unit: '',
    price_usd: '',
    price_input_usd: '',
    price_output_usd: ''
  });

  useEffect(() => {
    fetchCostData();
    fetchPricingConfig();
  }, []);

  const fetchCostData = async () => {
    try {
      const response = await api.get('/sessions/costs?days=30');
      setCostAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching cost analytics:', err);
      setError('Failed to load cost analytics');
    }
  };

  const fetchPricingConfig = async () => {
    try {
      const response = await api.get('/pricing');
      setPricingConfig(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching pricing config:', err);
      setError('Failed to load pricing configuration');
      setLoading(false);
    }
  };

  const initializeDefaultPricing = async () => {
    try {
      const response = await api.post('/pricing/initialize');
      alert(response.data.message);
      fetchPricingConfig();
    } catch (err) {
      console.error('Error initializing pricing:', err);
      alert('Failed to initialize pricing');
    }
  };

  const updatePricing = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newPricing,
        price_usd: parseFloat(newPricing.price_usd),
        price_input_usd: newPricing.price_input_usd ? parseFloat(newPricing.price_input_usd) : null,
        price_output_usd: newPricing.price_output_usd ? parseFloat(newPricing.price_output_usd) : null
      };
      
      await api.post('/pricing', payload);
      alert('Pricing updated successfully');
      setNewPricing({
        service_type: '',
        service_name: '',
        pricing_unit: '',
        price_usd: '',
        price_input_usd: '',
        price_output_usd: ''
      });
      fetchPricingConfig();
    } catch (err) {
      console.error('Error updating pricing:', err);
      alert('Failed to update pricing');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading cost data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="flex items-center justify-center gap-2 text-destructive mb-4">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
        <button
          onClick={() => {
            setLoading(true);
            setError(null);
            fetchCostData();
            fetchPricingConfig();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 mx-auto btn-hover-gradient"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  const sections = [
    { id: 'overview', name: 'Cost Overview', icon: BarChart3 },
    { id: 'pricing', name: 'Pricing Config', icon: DollarSign },
  ];

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground mb-2">Cost & Pricing</h1>
        <p className="text-muted-foreground">Manage costs and configure pricing for services</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6">
        {sections.map((section) => {
          const Icon = section.icon;
          return (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all rounded-full ${
              activeSection === section.id
                  ? 'bg-gradient-button text-primary-foreground shadow-lg shadow-primary/20'
                  : 'text-muted-foreground hover:text-foreground border border-border/60 hover:border-primary/30'
            }`}
          >
              <Icon className="w-4 h-4" />
            {section.name}
          </button>
          );
        })}
      </div>

      {/* Cost Overview Section */}
      {activeSection === 'overview' && costAnalytics && (
        <div className="flex-1 flex flex-col min-h-0 space-y-6 overflow-y-auto">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-5">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                  <DollarSign className="w-6 h-6 text-primary-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-2xl font-bold text-success">
                    ${costAnalytics.total_cost_usd}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Cost</div>
                  <div className="text-xs text-muted-foreground mt-1">{costAnalytics.total_sessions} sessions</div>
                </div>
              </div>
            </div>
            
            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-5">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                  <TrendingUp className="w-6 h-6 text-primary-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-2xl font-bold text-primary">
                    ${costAnalytics.avg_cost_per_session}
                  </div>
                  <div className="text-sm text-muted-foreground">Avg Cost/Session</div>
                </div>
              </div>
            </div>
            
            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-5">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                  <Phone className="w-6 h-6 text-primary-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-2xl font-bold text-primary">
                    ${costAnalytics.by_channel.phone.cost_usd}
                  </div>
                  <div className="text-sm text-muted-foreground">Phone Calls</div>
                  <div className="text-xs text-muted-foreground mt-1">{costAnalytics.by_channel.phone.sessions} sessions</div>
                </div>
              </div>
            </div>
            
            <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-5">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                  <Globe className="w-6 h-6 text-primary-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-2xl font-bold text-success">
                    ${costAnalytics.by_channel.web.cost_usd}
                  </div>
                  <div className="text-sm text-muted-foreground">Web Sessions</div>
                  <div className="text-xs text-muted-foreground mt-1">{costAnalytics.by_channel.web.sessions} sessions</div>
                </div>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Service Cost Breakdown
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <div className="text-center p-4 rounded-lg bg-background/30 border border-border/40">
                <div className="text-xl font-bold text-primary flex items-center justify-center gap-2 mb-1">
                  <Brain className="w-5 h-5" />
                  ${costAnalytics.cost_breakdown.llm_cost}
                </div>
                <div className="text-sm text-muted-foreground">LLM (AI Models)</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-background/30 border border-border/40">
                <div className="text-xl font-bold text-success flex items-center justify-center gap-2 mb-1">
                  <Mic className="w-5 h-5" />
                  ${costAnalytics.cost_breakdown.tts_cost}
                </div>
                <div className="text-sm text-muted-foreground">TTS (Voice Synthesis)</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-background/30 border border-border/40">
                <div className="text-xl font-bold text-yellow-500 flex items-center justify-center gap-2 mb-1">
                  <Mic className="w-5 h-5" />
                  ${costAnalytics.cost_breakdown.stt_cost}
                </div>
                <div className="text-sm text-muted-foreground">STT (Speech Recognition)</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-background/30 border border-border/40">
                <div className="text-xl font-bold text-primary flex items-center justify-center gap-2 mb-1">
                  <Zap className="w-5 h-5" />
                  ${costAnalytics.cost_breakdown.infrastructure_cost}
                </div>
                <div className="text-sm text-muted-foreground">LiveKit Infrastructure</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-background/30 border border-border/40">
                <div className="text-xl font-bold text-destructive flex items-center justify-center gap-2 mb-1">
                  <Phone className="w-5 h-5" />
                  ${costAnalytics.cost_breakdown.telephony_cost}
                </div>
                <div className="text-sm text-muted-foreground">Telephony (Phone)</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-background/30 border border-border/40">
                <div className="text-xl font-bold text-purple-400 flex items-center justify-center gap-2 mb-1">
                  <User className="w-5 h-5" />
                  ${costAnalytics.cost_breakdown.avatar_cost ?? 0}
                </div>
                <div className="text-sm text-muted-foreground">LiveAvatar (Video)</div>
              </div>
            </div>
          </div>

          {/* Agent Cost Breakdown */}
          {Object.keys(costAnalytics.by_agent).length > 0 && (
            <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Cost by Agent
              </h3>
              <div className="space-y-3">
                {Object.entries(costAnalytics.by_agent).map(([agentName, data]) => (
                  <div key={agentName} className="flex justify-between items-center py-3 px-4 rounded-lg bg-background/30 border border-border/40 hover:bg-background/40 transition-colors">
                    <div>
                      <div className="font-medium text-foreground">{agentName}</div>
                      <div className="text-sm text-muted-foreground">{data.sessions} sessions</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-success">${data.cost_usd}</div>
                      <div className="text-sm text-muted-foreground">${data.avg_cost}/session</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pricing Configuration Section */}
      {activeSection === 'pricing' && (
        <div className="flex-1 flex flex-col min-h-0 space-y-6 overflow-y-auto">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <DollarSign className="w-6 h-6" />
              Pricing Configuration
            </h2>
            {pricingConfig.length === 0 && (
              <button
                onClick={initializeDefaultPricing}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300"
              >
                <Plus className="w-4 h-4" />
                Initialize Default Pricing
              </button>
            )}
          </div>

          {/* Current Pricing Table */}
          {pricingConfig.length > 0 && (
            <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Current Pricing
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/60">
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Service Type</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Service Name</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Unit</th>
                      <th className="text-right py-3 px-4 text-muted-foreground font-medium">Price (USD)</th>
                      <th className="text-right py-3 px-4 text-muted-foreground font-medium">Input Price</th>
                      <th className="text-right py-3 px-4 text-muted-foreground font-medium">Output Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingConfig.map((pricing) => (
                      <tr key={pricing.id} className="border-b border-border/20 hover:bg-background/20 transition-colors">
                        <td className="py-3 px-4 text-foreground">{pricing.service_type}</td>
                        <td className="py-3 px-4 text-foreground">{pricing.service_name}</td>
                        <td className="py-3 px-4 text-muted-foreground">{pricing.pricing_unit}</td>
                        <td className="py-3 px-4 text-right text-success font-semibold">${pricing.price_usd}</td>
                        <td className="py-3 px-4 text-right text-primary">
                          {pricing.price_input_usd ? `$${pricing.price_input_usd}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-right text-primary">
                          {pricing.price_output_usd ? `$${pricing.price_output_usd}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Add/Update Pricing Form */}
          <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add/Update Pricing
            </h3>
            <form onSubmit={updatePricing} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Service Type</label>
                <AnimatedDropdown
                  value={newPricing.service_type}
                  onChange={(value) => {
                    const newServiceType = value;
                    // Set default pricing_unit based on service type
                    let defaultPricingUnit = newPricing.pricing_unit;
                    if (newServiceType === 'tts') {
                      // Default to per_1k_characters for TTS (unless it was per_minute, then clear it)
                      if (newPricing.pricing_unit === 'per_minute' || !newPricing.pricing_unit) {
                        defaultPricingUnit = 'per_1k_characters';
                      }
                    } else if (newServiceType === 'avatar') {
                      // LiveAvatar bills streaming minutes (1 credit = 1 minute)
                      defaultPricingUnit = 'per_minute';
                    } else if (newServiceType === 'llm') {
                      // Default to per_1k_tokens for LLM
                      if (!newPricing.pricing_unit || newPricing.pricing_unit === 'per_character' || newPricing.pricing_unit === 'per_1k_characters') {
                        defaultPricingUnit = 'per_1k_tokens';
                      }
                    }
                    
                    const updatedPricing = {
                      ...newPricing, 
                      service_type: newServiceType,
                      pricing_unit: defaultPricingUnit
                    };
                    setNewPricing(updatedPricing);
                  }}
                  options={[
                    { value: '', label: 'Select service type' },
                    { value: 'llm', label: 'LLM (AI Models)' },
                    { value: 'tts', label: 'TTS (Text-to-Speech)' },
                    { value: 'stt', label: 'STT (Speech-to-Text)' },
                    { value: 'telephony', label: 'Telephony' },
                    { value: 'infrastructure', label: 'Infrastructure (LiveKit)' },
                    { value: 'avatar', label: 'Avatar (LiveAvatar)' }
                  ]}
                  placeholder="Select service type"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Service Name</label>
                <input
                  type="text"
                  value={newPricing.service_name}
                  onChange={(e) => setNewPricing({...newPricing, service_name: e.target.value})}
                  placeholder="e.g., gpt-4o-mini, elevenlabs"
                  className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all placeholder:text-muted-foreground/50"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Pricing Unit</label>
                <AnimatedDropdown
                  value={newPricing.pricing_unit}
                  onChange={(value) => setNewPricing({...newPricing, pricing_unit: value})}
                  options={[
                    { value: '', label: 'Select unit' },
                    { value: 'per_1k_tokens', label: 'Per 1K Tokens' },
                    { value: 'per_1k_characters', label: 'Per 1K Characters' },
                    { value: 'per_character', label: 'Per Character (Legacy)' },
                    { 
                      value: 'per_minute', 
                      label: `Per Minute${newPricing.service_type === 'tts' ? ' (Disabled for TTS)' : ''}`,
                      disabled: newPricing.service_type === 'tts'
                    }
                  ]}
                  placeholder="Select unit"
                />
                {newPricing.service_type === 'tts' && (
                  <p className="text-xs text-yellow-400 mt-1">
                    Recommended: Use "Per 1K Characters" (same as "Per 1K Tokens" for LLM)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Base Price (USD)</label>
                <input
                  type="number"
                  step="0.000001"
                  value={newPricing.price_usd}
                  onChange={(e) => setNewPricing({...newPricing, price_usd: e.target.value})}
                  placeholder="0.000150"
                  className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all placeholder:text-muted-foreground/50"
                  required
                />
              </div>

              {newPricing.service_type === 'llm' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Input Price (USD)</label>
                    <input
                      type="number"
                      step="0.000001"
                      value={newPricing.price_input_usd}
                      onChange={(e) => setNewPricing({...newPricing, price_input_usd: e.target.value})}
                      placeholder="0.000150"
                      className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all placeholder:text-muted-foreground/50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Output Price (USD)</label>
                    <input
                      type="number"
                      step="0.000001"
                      value={newPricing.price_output_usd}
                      onChange={(e) => setNewPricing({...newPricing, price_output_usd: e.target.value})}
                      placeholder="0.000600"
                      className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all placeholder:text-muted-foreground/50"
                    />
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <button
                  type="submit"
                  className="w-full px-4 py-2.5 bg-gradient-button text-primary-foreground rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Update Pricing
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostDashboard;
